import { useState } from "react";
import { Sparkles, TrendingUp, TrendingDown, DollarSign, Users, Phone, Target, Loader2, Download, Calendar, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useAIAssistant } from "@/hooks/useAIAssistant";
import { toast } from "sonner";

interface WeeklyMetrics {
  leadsGenerated: number;
  leadsContacted: number;
  callsMade: number;
  emailsSent: number;
  meetingsBooked: number;
  proposalsSent: number;
  dealsWon: number;
  dealsLost: number;
  revenue: number;
  pipelineAdded: number;
}

interface AIWeeklyReportProps {
  metrics?: WeeklyMetrics;
  previousWeekMetrics?: WeeklyMetrics;
}

export function AIWeeklyReport({ metrics, previousWeekMetrics }: AIWeeklyReportProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [report, setReport] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const { chat } = useAIAssistant();

  const defaultMetrics: WeeklyMetrics = metrics || {
    leadsGenerated: 32,
    leadsContacted: 45,
    callsMade: 67,
    emailsSent: 156,
    meetingsBooked: 12,
    proposalsSent: 8,
    dealsWon: 4,
    dealsLost: 2,
    revenue: 85000,
    pipelineAdded: 120000,
  };

  const defaultPrevMetrics: WeeklyMetrics = previousWeekMetrics || {
    leadsGenerated: 28,
    leadsContacted: 40,
    callsMade: 58,
    emailsSent: 140,
    meetingsBooked: 10,
    proposalsSent: 6,
    dealsWon: 3,
    dealsLost: 3,
    revenue: 65000,
    pipelineAdded: 95000,
  };

  const calculateChange = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const generateReport = async () => {
    setIsGenerating(true);

    const prompt = `Generate a weekly sales performance report summary based on these metrics:

This Week:
- Leads Generated: ${defaultMetrics.leadsGenerated}
- Leads Contacted: ${defaultMetrics.leadsContacted}
- Calls Made: ${defaultMetrics.callsMade}
- Emails Sent: ${defaultMetrics.emailsSent}
- Meetings Booked: ${defaultMetrics.meetingsBooked}
- Proposals Sent: ${defaultMetrics.proposalsSent}
- Deals Won: ${defaultMetrics.dealsWon}
- Deals Lost: ${defaultMetrics.dealsLost}
- Revenue Closed: $${defaultMetrics.revenue.toLocaleString()}
- Pipeline Added: $${defaultMetrics.pipelineAdded.toLocaleString()}

Previous Week:
- Leads Generated: ${defaultPrevMetrics.leadsGenerated}
- Revenue Closed: $${defaultPrevMetrics.revenue.toLocaleString()}
- Deals Won: ${defaultPrevMetrics.dealsWon}

Provide:
1. Executive Summary (2-3 sentences highlighting key wins)
2. Top 3 Achievements
3. Top 3 Areas for Improvement
4. 3 Specific Action Items for Next Week
5. Motivational closing statement

Keep it concise and actionable. Use bullet points.`;

    const response = await chat(prompt);
    if (response) {
      setReport(response);
    }
    setIsGenerating(false);
  };

  const MetricItem = ({ 
    label, 
    value, 
    previousValue, 
    icon: Icon, 
    prefix = "" 
  }: { 
    label: string; 
    value: number; 
    previousValue: number; 
    icon: any;
    prefix?: string;
  }) => {
    const change = calculateChange(value, previousValue);
    const isPositive = change >= 0;

    return (
      <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-semibold">{prefix}{value.toLocaleString()}</span>
          <span className={cn(
            "text-xs flex items-center gap-0.5",
            isPositive ? "text-success" : "text-destructive"
          )}>
            {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {Math.abs(change)}%
          </span>
        </div>
      </div>
    );
  };

  const handleDownload = () => {
    if (!report) return;
    
    const content = `Weekly Sales Report - ${new Date().toLocaleDateString()}\n\n${report}`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `weekly-report-${new Date().toISOString().split("T")[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report downloaded");
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <BarChart3 className="h-4 w-4" />
          Weekly Report
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <BarChart3 className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <p className="font-semibold">AI Weekly Report</p>
              <p className="text-sm text-muted-foreground font-normal flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5" />
                Week of {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Key Metrics */}
          <div className="p-4 rounded-lg bg-muted/30">
            <h3 className="font-semibold text-sm mb-3">Key Metrics vs Previous Week</h3>
            <div className="space-y-1">
              <MetricItem label="Revenue" value={defaultMetrics.revenue} previousValue={defaultPrevMetrics.revenue} icon={DollarSign} prefix="$" />
              <MetricItem label="Deals Won" value={defaultMetrics.dealsWon} previousValue={defaultPrevMetrics.dealsWon} icon={Target} />
              <MetricItem label="Leads Generated" value={defaultMetrics.leadsGenerated} previousValue={defaultPrevMetrics.leadsGenerated} icon={Users} />
              <MetricItem label="Calls Made" value={defaultMetrics.callsMade} previousValue={defaultPrevMetrics.callsMade} icon={Phone} />
            </div>
          </div>

          <Separator />

          {/* AI Report */}
          {!report && !isGenerating ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mb-4">
                <Sparkles className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Generate AI Report</h3>
              <p className="text-muted-foreground text-sm mb-6 max-w-sm">
                Get AI-powered analysis of your weekly performance with actionable recommendations.
              </p>
              <Button onClick={generateReport} className="gap-2">
                <Sparkles className="h-4 w-4" />
                Generate Report
              </Button>
            </div>
          ) : isGenerating ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Analyzing your performance...</p>
            </div>
          ) : (
            <ScrollArea className="flex-1 h-[300px]">
              <div className="prose prose-sm dark:prose-invert max-w-none pr-4">
                <div className="whitespace-pre-wrap text-sm leading-relaxed">
                  {report}
                </div>
              </div>
            </ScrollArea>
          )}
        </div>

        {/* Actions */}
        {report && (
          <div className="flex items-center gap-2 pt-4 border-t border-border">
            <Button variant="outline" size="sm" onClick={handleDownload} className="gap-2">
              <Download className="h-4 w-4" />
              Download
            </Button>
            <Button variant="outline" size="sm" onClick={generateReport} disabled={isGenerating} className="gap-2">
              <Sparkles className="h-4 w-4" />
              Regenerate
            </Button>
            <div className="flex-1" />
            <Button onClick={() => setIsOpen(false)}>Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
